import React, { useEffect, useState } from "react";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CalendarToday,
  QueryStats,
  Schedule,
  Timeline,
} from "@mui/icons-material";
import { ordersEventsTimePattern } from "../../../../api/order";

const TimePatternAnalysis = ({ startDate, endDate }) => {
  const theme = useTheme();
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const weekdayNames = [
    "Segunda",
    "Terça",
    "Quarta",
    "Quinta",
    "Sexta",
    "Sábado",
    "Domingo",
  ];
  const eventLabels = {
    CFM: "Na Cozinha",
    PKD: "Aguardando Despacho",
    DSP: "Despachado",
  };

  const eventColors = {
    CFM: theme.palette.error.main,
    PKD: theme.palette.warning.main,
    DSP: theme.palette.error.main,
  };

  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (!startDate || !endDate) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
        });

        const response = await ordersEventsTimePattern(params);
        const data = await response.data;
        setAnalysisData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisData();
  }, [startDate, endDate]);

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const renderHourlyEventPattern = (hourPattern) => {
    if (Object.keys(hourPattern.eventPatterns).length === 0) {
      return;
    }

    const hasData = Object.keys(hourPattern.eventPatterns).length > 0;

    return (
      <Paper
        key={`hour-${hourPattern.hour}`}
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 3,
          backgroundColor: hasData
            ? alpha(theme.palette.primary.main, 0.02)
            : alpha(theme.palette.grey[300], 0.05),
          border: `1px solid ${
            hasData
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.grey[300], 0.2)
          }`,
          minHeight: 160,
          minWidth: 140,
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color={hasData ? "primary" : "text.secondary"}
          >
            {hourPattern.hour}:00
          </Typography>

          {hasData ? (
            <Stack spacing={1} width="100%">
              {Object.entries(hourPattern.eventPatterns).map(
                ([eventCode, data]) => (
                  <Box key={eventCode}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={0.5}
                    >
                      <Chip
                        label={eventLabels[eventCode]}
                        size="small"
                        sx={{
                          fontSize: "0.65rem",
                          height: 18,
                          backgroundColor: alpha(eventColors[eventCode], 0.1),
                          color: eventColors[eventCode],
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="caption" fontWeight={600}>
                        {formatTime(data.averageTime)}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        height: 4,
                        backgroundColor: alpha(eventColors[eventCode], 0.1),
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${Math.min(
                            (data.averageTime / 60) * 100,
                            100
                          )}%`,
                          backgroundColor: eventColors[eventCode],
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  </Box>
                )
              )}
            </Stack>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Sem dados
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderWeeklyEventPattern = (weekPattern) => {
    const hasData = Object.keys(weekPattern.eventPatterns).length > 0;

    return (
      <Paper
        key={`week-${weekPattern.weekday}`}
        elevation={2}
        sx={{
          p: 2.5,
          borderRadius: 3,
          backgroundColor: hasData
            ? alpha(theme.palette.secondary.main, 0.02)
            : alpha(theme.palette.grey[300], 0.05),
          border: `1px solid ${
            hasData
              ? alpha(theme.palette.secondary.main, 0.1)
              : alpha(theme.palette.grey[300], 0.2)
          }`,
          minHeight: 180,
        }}
      >
        <Stack spacing={2}>
          <Box textAlign="center">
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color={hasData ? "secondary.main" : "text.secondary"}
            >
              {weekdayNames[weekPattern.weekday]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {weekPattern.weekdayName}
            </Typography>
          </Box>

          {hasData ? (
            <Stack spacing={1.5}>
              {Object.entries(weekPattern.eventPatterns).map(
                ([eventCode, data]) => (
                  <Box key={eventCode}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: eventColors[eventCode],
                          }}
                        />
                        <Typography variant="caption" fontWeight={600}>
                          {eventLabels[eventCode]}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={eventColors[eventCode]}
                      >
                        {formatTime(data.averageTime)}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box textAlign="center" flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          Min
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                        >
                          {formatTime(data.minTime)}
                        </Typography>
                      </Box>
                      <Box textAlign="center" flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          Max
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                        >
                          {formatTime(data.maxTime)}
                        </Typography>
                      </Box>
                      <Box textAlign="center" flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          Eventos
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          fontWeight={600}
                        >
                          {data.totalEvents}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )
              )}
            </Stack>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Sem dados para este dia
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderLoadingState = () => (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<Timeline />}
        title="Análise de Padrões Temporais"
        sx={{ backgroundColor: theme.palette.secondary.main }}
        titleTypographyProps={{ sx: { color: "#ffffff" } }}
        sx={{ "& .MuiCardHeader-avatar": { color: "#ffffff" } }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress size={40} />
        </Box>
      </CardContent>
    </Card>
  );

  const renderErrorState = () => (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<Timeline />}
        title="Análise de Padrões Temporais"
        sx={{ backgroundColor: theme.palette.secondary.main }}
        titleTypographyProps={{ sx: { color: "#ffffff" } }}
        sx={{ "& .MuiCardHeader-avatar": { color: "#ffffff" } }}
      />
      <CardContent sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Erro ao carregar dados de padrões temporais: {error}
        </Alert>
      </CardContent>
    </Card>
  );

  if (loading) return renderLoadingState();
  if (error) return renderErrorState();
  if (!analysisData) return null;

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.secondary.main,
          0.02
        )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
            <Timeline />
          </Avatar>
        }
        title={
          <Typography variant="h6" fontWeight={700}>
            Análise de Padrões Temporais
          </Typography>
        }
        sx={{
          backgroundColor: theme.palette.secondary.main,
          color: "white",
          "& .MuiCardHeader-avatar": { mr: 2 },
        }}
      />

      <CardContent sx={{ p: 3 }}>
        {/* Summary Stats */}
        <Paper
          elevation={2}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.info.main,
              0.05
            )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                backgroundColor: theme.palette.info.main,
                width: 48,
                height: 48,
              }}
            >
              <QueryStats />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                Total de Eventos Analisados
              </Typography>
              <Typography variant="h5" fontWeight={700} color="info.main">
                {analysisData.totalEventsAnalyzed}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            aria-label="time pattern tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <Tab
              icon={<Schedule />}
              iconPosition="start"
              label="Padrões por Hora"
            />
            <Tab
              icon={<CalendarToday />}
              iconPosition="start"
              label="Padrões por Dia da Semana"
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {tabValue === 0 && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              color="text.primary"
            >
              Tempos de Processamento por Hora do Dia
            </Typography>
            <Box
              sx={{
                overflowX: "auto",
                pb: 2,
                "&::-webkit-scrollbar": {
                  height: 8,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.5),
                  borderRadius: 4,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.7),
                  },
                },
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ minWidth: "max-content", py: 1 }}
              >
                {analysisData.hourlyPatterns.map(renderHourlyEventPattern)}
              </Stack>
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              color="text.primary"
            >
              Tempos de Processamento por Dia da Semana
            </Typography>
            <Grid container spacing={2}>
              {analysisData.weeklyPatterns.map((weekPattern) => (
                <Grid item xs={12} sm={6} md={4} key={weekPattern.weekday}>
                  {renderWeeklyEventPattern(weekPattern)}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Legend */}
        <Stack
          direction="row"
          justifyContent="center"
          spacing={3}
          flexWrap="wrap"
        >
          {Object.entries(eventLabels).map(([code, label]) => (
            <Stack key={code} direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 1,
                  backgroundColor: eventColors[code],
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {label} ({code})
              </Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TimePatternAnalysis;
